import { useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Building2, Loader2 } from "lucide-react";

import { meetingsApi, projectsApi } from "@/api/services";
import type { EntityHomeOrg } from "@/api/types";
import { Button } from "@/components/ui/button";
import { switchActiveOrg } from "@/lib/switchActiveOrg";
import { cn } from "@/lib/utils";

export type CrossOrgKind = "project" | "meeting";

/** The member-only home-org read per kind: one server mechanism
 *  (app/services/entity_home_org.py), one route per entity. */
const HOME_ORG: Record<CrossOrgKind, (id: string) => Promise<EntityHomeOrg>> = {
  project: (id) => projectsApi.getHomeOrg(id),
  meeting: (id) => meetingsApi.getHomeOrg(id),
};

interface CrossOrgGateProps {
  kind: CrossOrgKind;
  entityId: string;
  /** Rendered when the caller is not a member of the entity's org, or the
   *  entity is already in their active org (switching would not help). */
  notFound: ReactNode;
  /** Cancel on the switch prompt. */
  onCancel: () => void;
  className?: string;
}

/**
 * Row 2b6d6836 (founder #3615 comment 6021603612, item 6): shown INSTEAD of a
 * page whose org-scoped read 404'd in the active org. A project link from
 * another org used to load the project shell with empty, wrong-org data, and a
 * meeting-invite link said "This meeting isn't available".
 *
 * Asks the entity's home-org route, which answers only a member of that org. A
 * member is asked "This <kind> is in <Org>. Switch to <Org>?" — asking rather
 * than switching silently, because a switch changes which org every other open
 * tab acts in. Switch reuses the org switcher's path (switchActiveOrg ->
 * POST /auth/switch-organization) and reloads the same URL. Anyone else sees
 * `notFound`.
 */
export function CrossOrgGate({ kind, entityId, notFound, onCancel, className }: CrossOrgGateProps) {
  const [switching, setSwitching] = useState(false);
  const [switchFailed, setSwitchFailed] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: [kind, entityId, "home-org"],
    queryFn: () => HOME_ORG[kind](entityId),
    retry: false,
    staleTime: 60_000,
  });

  if (isLoading) {
    return (
      <div className={cn("flex justify-center py-16", className)} role="status" aria-label={`Loading ${kind}`}>
        <Loader2 className="h-5 w-5 animate-spin opacity-60" />
      </div>
    );
  }

  if (!data || data.is_current) return <>{notFound}</>;

  const onSwitch = async () => {
    setSwitching(true);
    setSwitchFailed(false);
    try {
      const { pathname, search, hash } = window.location;
      await switchActiveOrg(data.org_id, `${pathname}${search}${hash}`);
    } catch {
      setSwitching(false);
      setSwitchFailed(true);
    }
  };

  return (
    <div
      className={cn("mx-auto max-w-md py-16 text-center", className)}
      role="dialog"
      aria-labelledby="cross-org-title"
      data-testid="cross-org-gate"
    >
      <Building2 className="mx-auto h-8 w-8 opacity-60" />
      <h2 id="cross-org-title" className="mt-4 text-lg font-semibold">
        This {kind} is in {data.org_name}.
      </h2>
      <p className="mt-2 text-sm opacity-80">Switch to {data.org_name}?</p>
      {switchFailed && (
        <p className="mt-2 text-sm text-red-600" role="alert">
          Could not switch organization. Please try again.
        </p>
      )}
      <div className="mt-6 flex justify-center gap-3">
        <Button variant="outline" onClick={onCancel} disabled={switching}>
          Cancel
        </Button>
        <Button onClick={onSwitch} disabled={switching}>
          {switching && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Switch to {data.org_name}
        </Button>
      </div>
    </div>
  );
}
